/**
 * Nub Tip Drawing — shared neon wireframe tip shapes for turret barrels.
 *
 * Each nub is drawn at (TL, 0) where TL = TURRET_LENGTH.
 * All shapes use the 3-pass neon stroke pattern for consistency.
 * Animated tips (vortex spiral, gyro propeller) return a child Graphics.
 */

import { Graphics } from 'pixi.js';
import { TANK_VISUAL } from '../constants';
import { lighten } from './shapes';

// Turret neon pass widths
const TURRET_PASSES = [
  { width: 5,   shift: 0,    alpha: 0.55 },
  { width: 3,   shift: 0.2,  alpha: 0.85 },
  { width: 1.2, shift: 0.45, alpha: 1.0 },
];

/**
 * Draw a turret nub tip at (TL, 0).
 * Returns a child Graphics for animated tips, or undefined.
 */
function drawNubTip(gfx: Graphics, nubId: string, color: number): Graphics | undefined {
  const TL = TANK_VISUAL.TURRET_LENGTH;
  const R = 5; // standard tip radius

  switch (nubId) {

    // ─── Original 9 ──────────────────────────────────────

    case 'mortar': {
      // Bore Ring — open stroked circle
      for (const pass of TURRET_PASSES) {
        gfx.circle(TL, 0, R);
        gfx.stroke({ color: lighten(color, pass.shift), width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'launcher': {
      // Arrow — forward chevron `>`
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.moveTo(TL - 4, -4); gfx.lineTo(TL + 4, 0); gfx.lineTo(TL - 4, 4);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'bouncer': {
      // Diamond — rotated square
      for (const pass of TURRET_PASSES) {
        gfx.moveTo(TL + R, 0); gfx.lineTo(TL, -R); gfx.lineTo(TL - R, 0); gfx.lineTo(TL, R);
        gfx.closePath();
        gfx.stroke({ color: lighten(color, pass.shift), width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'terraformer': {
      // Hexagon
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let v = 0; v < 6; v++) {
          const a = (Math.PI / 3) * v;
          const px = TL + Math.cos(a) * R, py = Math.sin(a) * R;
          if (v === 0) gfx.moveTo(px, py); else gfx.lineTo(px, py);
        }
        gfx.closePath();
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'scatter': {
      // Asterisk — 3 crossed lines
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI / 3) * s;
          gfx.moveTo(TL + Math.cos(a) * R, Math.sin(a) * R);
          gfx.lineTo(TL - Math.cos(a) * R, -Math.sin(a) * R);
        }
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'orbital': {
      // Reticle — circle + crosshairs
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.circle(TL, 0, R);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
        gfx.moveTo(TL - R, 0); gfx.lineTo(TL + R, 0);
        gfx.moveTo(TL, -R); gfx.lineTo(TL, R);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    case 'vortex': {
      // Spiral — animated child
      const tipChild = new Graphics();
      const turns = 1.5, steps = 24;
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const angle = t * turns * Math.PI * 2;
          const radius = 1 + t * (R - 1);
          const px = Math.cos(angle) * radius, py = Math.sin(angle) * radius;
          if (s === 0) tipChild.moveTo(px, py); else tipChild.lineTo(px, py);
        }
        tipChild.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      tipChild.position.set(TL, 0);
      return tipChild;
    }

    case 'gyro': {
      // Propeller — 3 blades, animated child
      const tipChild = new Graphics();
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI * 2 / 3) * s;
          tipChild.moveTo(0, 0);
          tipChild.lineTo(Math.cos(a) * R, Math.sin(a) * R);
        }
        tipChild.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      tipChild.position.set(TL, 0);
      return tipChild;
    }

    // duelist is handled separately (saber replaces entire turret)

    // ─── Custom 30 ───────────────────────────────────────

    case 'trident': {
      // 3-pronged fork — center spike + two angled prongs
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Center prong
        gfx.moveTo(TL, 0); gfx.lineTo(TL + 7, 0);
        // Top prong
        gfx.moveTo(TL, -3); gfx.lineTo(TL + 6, -5);
        // Bottom prong
        gfx.moveTo(TL, 3); gfx.lineTo(TL + 6, 5);
        // Crossbar
        gfx.moveTo(TL, -4); gfx.lineTo(TL, 4);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'claw': {
      // Two curved hooks
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Top hook
        gfx.moveTo(TL, -1); gfx.quadraticCurveTo(TL + 7, -2, TL + 5, -6);
        // Bottom hook
        gfx.moveTo(TL, 1); gfx.quadraticCurveTo(TL + 7, 2, TL + 5, 6);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'crown': {
      // 3-point crown
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.moveTo(TL - 4, 3); gfx.lineTo(TL - 4, -1);
        gfx.lineTo(TL - 2, 1); gfx.lineTo(TL, -4);
        gfx.lineTo(TL + 2, 1); gfx.lineTo(TL + 4, -1);
        gfx.lineTo(TL + 4, 3);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'anchor': {
      // Ship anchor
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Vertical shaft
        gfx.moveTo(TL, -5); gfx.lineTo(TL, 4);
        // Crossbar at top
        gfx.moveTo(TL - 3, -3); gfx.lineTo(TL + 3, -3);
        // Curved flukes
        gfx.moveTo(TL - 4, 1); gfx.quadraticCurveTo(TL - 4, 5, TL, 4);
        gfx.moveTo(TL + 4, 1); gfx.quadraticCurveTo(TL + 4, 5, TL, 4);
        // Ring at top
        gfx.circle(TL, -6, 1.5);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'crescent': {
      // Moon crescent
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Outer arc
        gfx.arc(TL, 0, R, -Math.PI * 0.7, Math.PI * 0.7);
        // Inner arc (cutout effect, drawn as opposing arc)
        gfx.moveTo(TL + 1, -4);
        gfx.arc(TL + 2, 0, 4, -Math.PI * 0.6, Math.PI * 0.6);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'lightning': {
      // Zigzag bolt
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.moveTo(TL - 2, -5);
        gfx.lineTo(TL + 1, -1);
        gfx.lineTo(TL - 1, 0);
        gfx.lineTo(TL + 3, 1);
        gfx.lineTo(TL, 2);
        gfx.lineTo(TL + 2, 5);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'flame': {
      // Wavy flame tongues
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Center flame
        gfx.moveTo(TL - 1, 0);
        gfx.quadraticCurveTo(TL + 3, -3, TL + 6, 0);
        gfx.quadraticCurveTo(TL + 3, 3, TL - 1, 0);
        // Top lick
        gfx.moveTo(TL, -2);
        gfx.quadraticCurveTo(TL + 2, -5, TL + 4, -3);
        // Bottom lick
        gfx.moveTo(TL, 2);
        gfx.quadraticCurveTo(TL + 2, 5, TL + 4, 3);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'snowflake': {
      // 6-arm crystal with branches
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 6; s++) {
          const a = (Math.PI / 3) * s;
          const cos = Math.cos(a), sin = Math.sin(a);
          // Main arm
          gfx.moveTo(TL, 0);
          gfx.lineTo(TL + cos * 6, sin * 6);
          // Branch at 60% along arm
          const bx = TL + cos * 3.5, by = sin * 3.5;
          const ba = a + Math.PI / 4;
          gfx.moveTo(bx, by);
          gfx.lineTo(bx + Math.cos(ba) * 2, by + Math.sin(ba) * 2);
          const ba2 = a - Math.PI / 4;
          gfx.moveTo(bx, by);
          gfx.lineTo(bx + Math.cos(ba2) * 2, by + Math.sin(ba2) * 2);
        }
        gfx.stroke({ color: c, width: pass.width * 0.8, alpha: pass.alpha });
      }
      break;
    }

    case 'crosshair_dot': {
      // Crosshair with prominent center dot
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Cross lines (with gap at center)
        gfx.moveTo(TL - 6, 0); gfx.lineTo(TL - 2, 0);
        gfx.moveTo(TL + 2, 0); gfx.lineTo(TL + 6, 0);
        gfx.moveTo(TL, -6); gfx.lineTo(TL, -2);
        gfx.moveTo(TL, 2); gfx.lineTo(TL, 6);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        // Center dot
        gfx.circle(TL, 0, 1.5);
        gfx.fill({ color: c, alpha: pass.alpha });
      }
      break;
    }

    case 'atom': {
      // 3 elliptical orbits + center nucleus
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI / 3) * s;
          const cos = Math.cos(a), sin = Math.sin(a);
          const steps = 16;
          for (let i = 0; i <= steps; i++) {
            const t = (i / steps) * Math.PI * 2;
            const ex = Math.cos(t) * 5;
            const ey = Math.sin(t) * 2;
            const px = TL + ex * cos - ey * sin;
            const py = ex * sin + ey * cos;
            if (i === 0) gfx.moveTo(px, py); else gfx.lineTo(px, py);
          }
          gfx.stroke({ color: c, width: pass.width * 0.6, alpha: pass.alpha });
        }
        // Nucleus
        gfx.circle(TL, 0, 1.5);
        gfx.fill({ color: c, alpha: pass.alpha });
      }
      break;
    }

    case 'eye': {
      // Evil eye — almond shape with circle iris
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Almond outline
        gfx.moveTo(TL - 6, 0);
        gfx.quadraticCurveTo(TL, -5, TL + 6, 0);
        gfx.quadraticCurveTo(TL, 5, TL - 6, 0);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
        // Iris
        gfx.circle(TL, 0, 2);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        // Pupil
        gfx.circle(TL, 0, 0.8);
        gfx.fill({ color: c, alpha: pass.alpha });
      }
      break;
    }

    case 'hook': {
      // Pirate hook — curved J shape
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.moveTo(TL - 2, -3);
        gfx.lineTo(TL, -3);
        gfx.quadraticCurveTo(TL + 6, -3, TL + 6, 2);
        gfx.quadraticCurveTo(TL + 6, 6, TL + 1, 5);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'skull': {
      // Skull outline
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Cranium (circle arc)
        gfx.arc(TL, -1, 4.5, -Math.PI * 0.8, Math.PI * 0.1);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
        // Jaw
        gfx.moveTo(TL - 3, 2); gfx.lineTo(TL + 2, 2);
        gfx.lineTo(TL + 3, 4); gfx.lineTo(TL - 2, 4);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        // Eyes
        gfx.circle(TL - 1.5, -1.5, 1);
        gfx.circle(TL + 1.5, -1.5, 1);
        gfx.fill({ color: c, alpha: pass.alpha * 0.7 });
      }
      break;
    }

    case 'heart': {
      // Heart shape
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        gfx.moveTo(TL, 4);
        gfx.quadraticCurveTo(TL - 6, 0, TL - 3, -3);
        gfx.quadraticCurveTo(TL, -5, TL, -1);
        gfx.quadraticCurveTo(TL, -5, TL + 3, -3);
        gfx.quadraticCurveTo(TL + 6, 0, TL, 4);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'music_note': {
      // 8th note
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Stem
        gfx.moveTo(TL + 2, -5); gfx.lineTo(TL + 2, 3);
        // Flag
        gfx.moveTo(TL + 2, -5);
        gfx.quadraticCurveTo(TL + 6, -3, TL + 4, 0);
        // Note head
        gfx.ellipse(TL, 3, 2.5, 1.8);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'biohazard': {
      // 3-arc biohazard symbol
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI * 2 / 3) * s - Math.PI / 2;
          gfx.arc(TL + Math.cos(a) * 2.5, Math.sin(a) * 2.5, 4,
            a - Math.PI * 0.4, a + Math.PI * 0.4);
          gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        }
        // Center ring
        gfx.circle(TL, 0, 1.5);
        gfx.stroke({ color: c, width: pass.width * 0.5, alpha: pass.alpha });
      }
      break;
    }

    case 'radiation': {
      // Radiation trefoil — 3 pie wedges
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI * 2 / 3) * s - Math.PI / 2;
          gfx.moveTo(TL + Math.cos(a - 0.3) * 2, Math.sin(a - 0.3) * 2);
          gfx.lineTo(TL + Math.cos(a - 0.3) * 5.5, Math.sin(a - 0.3) * 5.5);
          gfx.arc(TL, 0, 5.5, a - 0.3, a + 0.3);
          gfx.lineTo(TL + Math.cos(a + 0.3) * 2, Math.sin(a + 0.3) * 2);
          gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        }
        // Center circle
        gfx.circle(TL, 0, 1.8);
        gfx.stroke({ color: c, width: pass.width * 0.5, alpha: pass.alpha });
      }
      break;
    }

    case 'gear': {
      // 8-tooth gear
      const teeth = 8;
      const inner = 3, outer = 5.5;
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < teeth; s++) {
          const a1 = (Math.PI * 2 / teeth) * s;
          const a2 = a1 + Math.PI / teeth * 0.4;
          const a3 = a1 + Math.PI / teeth * 0.6;
          const a4 = a1 + Math.PI / teeth;
          if (s === 0) gfx.moveTo(TL + Math.cos(a1) * inner, Math.sin(a1) * inner);
          gfx.lineTo(TL + Math.cos(a2) * inner, Math.sin(a2) * inner);
          gfx.lineTo(TL + Math.cos(a2) * outer, Math.sin(a2) * outer);
          gfx.lineTo(TL + Math.cos(a3) * outer, Math.sin(a3) * outer);
          gfx.lineTo(TL + Math.cos(a3) * inner, Math.sin(a3) * inner);
          gfx.lineTo(TL + Math.cos(a4) * inner, Math.sin(a4) * inner);
        }
        gfx.closePath();
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    case 'saw': {
      // Circular saw — ring with outward teeth
      const teeth = 10;
      const inner = 3, outer = 6;
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < teeth; s++) {
          const a1 = (Math.PI * 2 / teeth) * s;
          const a2 = a1 + Math.PI / teeth;
          if (s === 0) gfx.moveTo(TL + Math.cos(a1) * outer, Math.sin(a1) * outer);
          else gfx.lineTo(TL + Math.cos(a1) * outer, Math.sin(a1) * outer);
          gfx.lineTo(TL + Math.cos(a2) * inner, Math.sin(a2) * inner);
        }
        gfx.closePath();
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    case 'drill': {
      // Spiral drill — tight spiral going forward
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        const steps = 20;
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const angle = t * Math.PI * 4; // 2 full rotations
          const r = (1 - t) * 5; // tapers to point
          const px = TL + t * 8;
          const py = Math.sin(angle) * r;
          if (s === 0) gfx.moveTo(px, py); else gfx.lineTo(px, py);
        }
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'mace': {
      // Spiked ball on stem
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Ball
        gfx.circle(TL + 2, 0, 3);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
        // Spikes
        const spikes = 6;
        for (let s = 0; s < spikes; s++) {
          const a = (Math.PI * 2 / spikes) * s;
          gfx.moveTo(TL + 2 + Math.cos(a) * 3, Math.sin(a) * 3);
          gfx.lineTo(TL + 2 + Math.cos(a) * 6, Math.sin(a) * 6);
        }
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    case 'hammer': {
      // Thor hammer — T-shape
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Handle extension
        gfx.moveTo(TL - 2, 0); gfx.lineTo(TL + 2, 0);
        // Hammer head (thick horizontal bar)
        gfx.moveTo(TL + 2, -5); gfx.lineTo(TL + 2, 5);
        gfx.lineTo(TL + 5, 5); gfx.lineTo(TL + 5, -5);
        gfx.closePath();
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'scythe': {
      // Curved blade
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Shaft
        gfx.moveTo(TL - 2, 3); gfx.lineTo(TL + 1, -2);
        // Blade curve
        gfx.moveTo(TL + 1, -2);
        gfx.quadraticCurveTo(TL + 7, -6, TL + 6, 0);
        gfx.quadraticCurveTo(TL + 5, 3, TL + 1, 1);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'shuriken': {
      // 4-point ninja star
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 4; s++) {
          const a = (Math.PI / 2) * s + Math.PI / 4;
          const na = a + Math.PI / 4;
          gfx.moveTo(TL, 0);
          gfx.lineTo(TL + Math.cos(a) * 6, Math.sin(a) * 6);
          gfx.lineTo(TL + Math.cos(na) * 2, Math.sin(na) * 2);
        }
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    case 'wings': {
      // Spread wing pair
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Top wing
        gfx.moveTo(TL, 0);
        gfx.quadraticCurveTo(TL + 3, -2, TL + 6, -5);
        gfx.quadraticCurveTo(TL + 4, -2, TL + 2, -1);
        // Bottom wing
        gfx.moveTo(TL, 0);
        gfx.quadraticCurveTo(TL + 3, 2, TL + 6, 5);
        gfx.quadraticCurveTo(TL + 4, 2, TL + 2, 1);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'horns': {
      // Devil horns — two curved spikes going up
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Left horn
        gfx.moveTo(TL - 2, 1);
        gfx.quadraticCurveTo(TL - 4, -4, TL - 1, -6);
        // Right horn
        gfx.moveTo(TL + 2, 1);
        gfx.quadraticCurveTo(TL + 4, -4, TL + 1, -6);
        // Base connector
        gfx.moveTo(TL - 2, 1); gfx.lineTo(TL + 2, 1);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
      }
      break;
    }

    case 'halo': {
      // Angel ring — ellipse floating above tip
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Halo ellipse (tilted)
        gfx.ellipse(TL, -2, 5, 2);
        gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
        // Small cross below
        gfx.moveTo(TL, 1); gfx.lineTo(TL, 4);
        gfx.moveTo(TL - 1.5, 2.5); gfx.lineTo(TL + 1.5, 2.5);
        gfx.stroke({ color: c, width: pass.width * 0.6, alpha: pass.alpha });
      }
      break;
    }

    case 'clover': {
      // 3-leaf clover
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        for (let s = 0; s < 3; s++) {
          const a = (Math.PI * 2 / 3) * s - Math.PI / 2;
          const cx = TL + Math.cos(a) * 3;
          const cy = Math.sin(a) * 3;
          gfx.circle(cx, cy, 2.5);
        }
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        // Stem
        gfx.moveTo(TL, 2); gfx.lineTo(TL - 1, 6);
        gfx.stroke({ color: c, width: pass.width * 0.5, alpha: pass.alpha });
      }
      break;
    }

    case 'paw': {
      // Paw print — 3 toe pads + palm pad
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        // Palm pad
        gfx.ellipse(TL, 1, 3, 2.5);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
        // Toe pads
        gfx.circle(TL - 2.5, -2.5, 1.3);
        gfx.circle(TL, -3.5, 1.3);
        gfx.circle(TL + 2.5, -2.5, 1.3);
        gfx.stroke({ color: c, width: pass.width * 0.6, alpha: pass.alpha });
      }
      break;
    }

    case 'pixel': {
      // 2x2 pixel block (retro look)
      for (const pass of TURRET_PASSES) {
        const c = lighten(color, pass.shift);
        const s = 3; // pixel size
        gfx.rect(TL - s, -s, s, s);
        gfx.rect(TL, -s, s, s);
        gfx.rect(TL - s, 0, s, s);
        gfx.rect(TL, 0, s, s);
        gfx.stroke({ color: c, width: pass.width * 0.7, alpha: pass.alpha });
      }
      break;
    }

    default: {
      // Fallback — standard filled circle
      for (const pass of TURRET_PASSES) {
        gfx.circle(TL, 0, TANK_VISUAL.TIP_RADIUS);
        gfx.stroke({ color: lighten(color, pass.shift), width: pass.width, alpha: pass.alpha });
      }
      break;
    }
  }

  return undefined;
}

/**
 * Draw a complete barrel line + nub tip (non-Duelist tanks).
 * Used by both game rendering and skins shop preview.
 */
export function drawBarrel(gfx: Graphics, nubId: string, color: number): Graphics | undefined {
  const TL = TANK_VISUAL.TURRET_LENGTH;

  // Standard 3-pass neon line from (0,0) to (TL, 0)
  for (const pass of TURRET_PASSES) {
    const c = lighten(color, pass.shift);
    gfx.moveTo(0, 0);
    gfx.lineTo(TL, 0);
    gfx.stroke({ color: c, width: pass.width, alpha: pass.alpha });
  }

  return drawNubTip(gfx, nubId, color);
}

/**
 * Get the animated tip rotation speed for a given nub ID.
 */
export function getNubAnimSpeed(nubId: string): number {
  return nubId === 'vortex' ? 1.0 : 2.0;
}
